import IosShareIcon from "@mui/icons-material/IosShare";
import {
  Box,
  Button,
  styled,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AdvancedSearch from "controls/global/advanced-search/AdvancedSearch";

import ColumnsConfig, {
  MenuColumnItem,
} from "controls/global/columns-config/ColumnsConfig";
import React, {
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { AdvancedSearchHandle } from "controls/global/advanced-search/AdvancedSearch";
import {
  colors,
  gaps,
  iconSize,
  padding,
} from "theme/defaultStyle";
import {
  PERMISSIONS,
  PageType,
} from "utils/data/enum";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { InventoryLookupUI } from "entities/UIModel/InventoryLookupUI";

import { orderBy } from "lodash";
import { SelectedHeader } from "pages/components/SelectedHeader";
import useLookupStore from "utils/context/InventoryLookupContext";
import useStewartTableStore from "utils/context/StewartTableContext";
import { InventoryLookupSearchCriteria } from "entities/UIModel/InventoryLookupSearchCriteria ";
// import ActionButton from "controls/global/action-button";
import InventoryVerifyDialog from "./inventory-verify/InventoryVerifyDialog";
import FilterActions from "./FilterActions";
import { EmptyStateType } from "controls/global/stewart-table/StewartTableBodyEmpty";

interface Props {
  columns: MenuColumnItem<InventoryLookupUI>[];
  hiddenColumns: (keyof InventoryLookupUI)[];
  updateHiddenColumns?: (columns: (keyof InventoryLookupUI)[]) => void;
  handleAdvancedFilterChange: (name: keyof InventoryLookupSearchCriteria, value: any, execSearch: boolean) => void;
  handleAdvanceSearchPanelOpen: (open: boolean) => void;
  storedCriteria?: any;
  onExport?: any;
  setOpenExportDialog: (open: boolean) => void;
  onReset: () => void;
  onApply: () => void;
  disableApply: () => boolean;
  advancedSearchRef?: RefObject<AdvancedSearchHandle>;
  emptyStateType?: EmptyStateType;
}

const Container = styled("div")({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: gaps.large1,
});

const ActionButtonGroup = styled(Box)({
  display: "flex",
  gap: gaps.large1,
  justifyContent: "flex-end",
  flexShrink: 0,
  "& .MuiButton-root": {
    color: colors.blue15,
    display: "flex",
    gap: gaps.small1,
    paddingBottom: padding.xsmall1,
    paddingLeft: padding.xsmall2,
    paddingRight: padding.xsmall2,
    paddingTop: padding.xsmall1,
    textTransform: "none",
    "&.Mui-disabled": {
      background: colors.white,
      color: colors.blue12,
      opacity: 1,
    },
    "& .MuiSvgIcon-root": {
      fontSize: iconSize.medium,
    },
    "& .rotate-90": {
      transform: "rotate(90deg)",
    },
  },
});

const TextStyles = styled("span")({
  fontWeight: 600,
  display: 'flex', 
  alignItems: 'center', 
  gap: gaps.small1
});

const StyledTableDescription = styled(Typography)({
  alignSelf: "flex-start",
});

const TABLE_DESCRIPTION = "Locate a policy by entering search criteria in the columns below or use the Advanced button, then click Apply Filters.";

const InventoryLookupHeader = ({
  columns,
  hiddenColumns,
  updateHiddenColumns,
  handleAdvancedFilterChange,
  handleAdvanceSearchPanelOpen,
  onExport,
  setOpenExportDialog,
  onReset,
  onApply,
  disableApply,
  advancedSearchRef,
  emptyStateType,
}: Props) => {
  const [, { setColumnDefinition, refresh },] = useLookupStore();
  const [, { setAdvanceSearch, setMaxDate, setMinDate }] = useStewartTableStore();
  const [{ showToast }, {setShowToast}] = useStewartTableStore();

  const [advancedSearchOpen, setAdvancedSearchOpen] = useState<boolean>(false);
  const [sortedColumns, setSortedColumn] = useState<any>([]);
  const advancedButtonRef: RefObject<HTMLButtonElement> = useRef(null);
    const [openInventoryVerifyDialog, setOpenInventoryVerifyDialog] = useState<boolean>(false);
  const handleSearchPanel = (open: boolean) => {
    setAdvanceSearch(open);
    setAdvancedSearchOpen(open);
    handleAdvanceSearchPanelOpen(open);
    if (!open && showToast) setShowToast(false);
  };
  const [{ selectedLookupItems }] = useLookupStore();
  // Permission
  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];
  
  // const hasInventoryVerifyAccess = permissionCodes.includes(PERMISSIONS.INV_VERIFY_ACCESS);

  useEffect(() => {
    setMinDate(null);
    setMaxDate(null);
  }, [advancedSearchOpen]);

  const handleColumnChecked = (id: keyof InventoryLookupUI, checked: boolean) => {
    if (checked) {
      updateHiddenColumns?.([...hiddenColumns, id]);
    } else {
      updateHiddenColumns?.([...hiddenColumns].filter((col) => col !== id));
    }
    setColumnDefinition([id], "hidden", checked);
  };

  const handleHideAllColumns = () => {
    if (hiddenColumns.length === sortedColumns.length) return;
    const fieldIdsExceptFirst = sortedColumns.slice(1).map((col: any) => col.field);
    updateHiddenColumns?.(fieldIdsExceptFirst);
    setColumnDefinition(fieldIdsExceptFirst, "hidden", true);
  };

  const handleShowAllColumns = () => {
    if (hiddenColumns.length === 0) return;
    const fieldIds = columns.map((c) => c.field);
    updateHiddenColumns?.([]);
    setColumnDefinition(fieldIds, "hidden", false);
  };

  const handleExport = () => {
    onExport?.();
  };

  useEffect(() => {
    const sortedColumns = orderBy(columns, "name");
    setSortedColumn(sortedColumns);
  }, [columns]);

  const closeInventoryVerifyDialog = () => {
    refresh();
    setOpenInventoryVerifyDialog(false);
  }

  return (
    <>
      <Container>
        <StyledTableDescription className="subheading">
          {TABLE_DESCRIPTION}
        </StyledTableDescription>
        <ActionButtonGroup>
          <FilterActions onReset={onReset} onApply={onApply} disableApply={disableApply} />
        <Button
            style={{
              backgroundColor: advancedSearchOpen ? colors.blue13 : colors.white,
              opacity: 1,
            }}
            onClick={() => handleSearchPanel(!advancedSearchOpen)}
            data-test="lookupHeader"
            ref={advancedButtonRef}
          >
            <TextStyles>
              Advanced
              <SearchIcon />
            </TextStyles>
          </Button>
          <ColumnsConfig
            options={columns}
            onChecked={handleColumnChecked}
            onHideAll={handleHideAllColumns}
            onShowAll={handleShowAllColumns}
          />
          <Button
            disabled={!permissionCodes?.includes(PERMISSIONS.EXPORT_DATA_POLICY) || emptyStateType === EmptyStateType.NO_FILTERS || emptyStateType === EmptyStateType.NO_RESULTS}
            style={{
              opacity: 1,
            }}
            onClick={() =>
              selectedLookupItems.length === 0 ? handleExport() : setOpenExportDialog(true)
            }        >
            <TextStyles>
              Export
              <IosShareIcon className="icon rotate-90" />
            </TextStyles>
          </Button>
          {/* <ActionButton
            variant="outlined"
            color="primary"
            disabled={!hasInventoryVerifyAccess}
            onClick={() => setOpenInventoryVerifyDialog(true)}
          >
            Verify Inventory
          </ActionButton> */}
        </ActionButtonGroup>

      </Container>
        <AdvancedSearch
          ref={advancedSearchRef}
          key={`adv_search_${advancedSearchOpen}`}
          open={advancedSearchOpen}
          handleFilterChange={advancedSearchOpen ? handleAdvancedFilterChange : () => { }}
          page={PageType.LookupPage}
        />
      {selectedLookupItems?.length > 0 && (
        <SelectedHeader page={PageType.LookupPage} />
      )}
      <InventoryVerifyDialog 
        isOpen={openInventoryVerifyDialog}
        onClose={closeInventoryVerifyDialog}
      />
    </>
  );
};

export default InventoryLookupHeader;
